import React from "react";
import { Card, Alert } from "react-bootstrap";

function ManagingGroupMembers() {
  return (
    <div>
      <h2 className="mb-4">
        Managing Group Members
      </h2>

      <Alert variant="info">
        Learn how to add and remove users from permission groups to control who has access.
      </Alert>

      <Card className="mb-4">
        <Card.Body>
          <h4>Step-by-Step: Adding Users to a Group</h4>
          <ol>
            <li>Go to <strong>Permissions</strong> in the Settings section</li>
            <li>Click on a permission group in the table to select it</li>
            <li>Click the <strong>Manage Users</strong> button</li>
            <li>A modal will open showing all users in your organization</li>
            <li>Check the boxes next to users you want to add to this group</li>
            <li>Uncheck boxes to remove users from the group</li>
            <li>Changes are saved automatically as you check/uncheck boxes</li>
          </ol>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Understanding Multiple Group Membership</h4>
          <p>
            Users can belong to multiple groups simultaneously. This is often useful and intentional:
          </p>
          <ul>
            <li><strong>Permissions are Cumulative</strong> - Users get all permissions from all their groups combined</li>
            <li><strong>Runbook Access is Cumulative</strong> - Users see runbooks from all their groups</li>
            <li><strong>No Conflicts</strong> - More permissions is always "more access", never "less access"</li>
          </ul>

          <h6 className="mt-3">Example Scenario:</h6>
          <p>Sarah is in two groups:</p>
          <ul>
            <li><strong>"Finance Team"</strong> - Has <code>runbooks.execute</code> and access to 5 finance runbooks</li>
            <li><strong>"Read-Only Users"</strong> - Has <code>variables.view</code>, <code>history.view</code> and access to 0 additional runbooks</li>
          </ul>
          <p className="mt-2">
            <strong>Result:</strong> Sarah can execute runbooks, view variables, view history, and see 5 finance runbooks.
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Removing Users from a Group</h4>
          <p>To remove a user from a group:</p>
          <ol>
            <li>Select the group</li>
            <li>Click <strong>Manage Users</strong></li>
            <li>Uncheck the box next to the user's name</li>
            <li>The change takes effect immediately</li>
          </ol>
          <Alert variant="warning" className="mt-3">
            <strong>Important:</strong> If you remove a user from their only group, they may lose access to all runbooks.
            Make sure they're in at least one other appropriate group.
          </Alert>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Checking a User's Group Memberships</h4>
          <p>To see which groups a user belongs to:</p>
          <ol>
            <li>Go through each group and click <strong>Manage Users</strong></li>
            <li>Look for checkmarks next to the user's name</li>
          </ol>
          <p className="mt-3">
            <strong>Tip:</strong> Keep track of user group memberships in the group descriptions or in separate documentation.
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Common Group Membership Patterns</h4>

          <h6 className="mt-3">Single Group Membership</h6>
          <p><strong>When to use:</strong> User has a clear, single role</p>
          <p><strong>Example:</strong></p>
          <ul>
            <li>John is a finance operator → "Finance Operators" group only</li>
            <li>Simple, clear access</li>
          </ul>

          <h6 className="mt-3">Multiple Group Membership</h6>
          <p><strong>When to use:</strong> User has multiple roles or needs</p>
          <p><strong>Example:</strong></p>
          <ul>
            <li>Mary is a developer who also works on finance projects</li>
            <li>Member of "Developers" (for creation permissions)</li>
            <li>Member of "Finance Team" (for finance runbook access)</li>
            <li>Gets full developer permissions + access to finance runbooks</li>
          </ul>

          <h6 className="mt-3">Base + Specialized Groups</h6>
          <p><strong>When to use:</strong> All users need some base access, plus role-specific access</p>
          <p><strong>Example:</strong></p>
          <ul>
            <li>All users in "Base Access" group (history.view, variables.view, connectors.view)</li>
            <li>Plus role-specific groups like "Finance Operators", "HR Operators", etc.</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Onboarding New Users</h4>
          <p>When a new user joins your organization:</p>
          <ol>
            <li>Identify their role and responsibilities</li>
            <li>Find existing groups that match their needs</li>
            <li>Add them to those groups</li>
            <li>Have them log in and verify they can access what they need</li>
          </ol>
          <p className="mt-3">
            If no existing group fits, consider creating a new group rather than giving them custom access.
            This makes it easier to onboard future users in the same role.
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Offboarding Users</h4>
          <p>When a user leaves or changes roles:</p>
          <ul>
            <li><strong>Option 1: Remove from All Groups</strong> - They lose all access but remain in the system</li>
            <li><strong>Option 2: Delete the User</strong> - Completely remove them (Admin feature)</li>
            <li><strong>Option 3: Change Group Memberships</strong> - Move them to different groups for their new role</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Best Practices</h4>
          <ul>
            <li><strong>Document Membership Decisions</strong> - Note why users are in specific groups</li>
            <li><strong>Regular Reviews</strong> - Quarterly, review who's in each group and remove users who no longer need access</li>
            <li><strong>Test After Adding</strong> - Have the user verify they can complete their tasks</li>
            <li><strong>Principle of Least Privilege</strong> - Only add users to groups they actually need</li>
            <li><strong>Use Consistent Patterns</strong> - All users in the same role should be in the same groups</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Troubleshooting</h4>

          <h6 className="mt-3">User Can't See Any Runbooks</h6>
          <ul>
            <li>Check if they're in at least one group</li>
            <li>Check if their groups have runbooks assigned</li>
            <li>Verify the groups are marked as Active</li>
          </ul>

          <h6 className="mt-3">User Can't Perform an Action</h6>
          <ul>
            <li>Check if any of their groups have the required permission</li>
            <li>Verify they can see the runbook (must be assigned to their group)</li>
          </ul>

          <h6 className="mt-3">Changes Not Taking Effect</h6>
          <ul>
            <li>Changes are immediate - no logout required</li>
            <li>Have the user refresh their browser</li>
            <li>Verify the group is Active</li>
          </ul>
        </Card.Body>
      </Card>
    </div>
  );
}

export default ManagingGroupMembers;
